import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { AUTH_ENABLED } from '../../config/features';

export function HeroSection() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.15),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.25),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(14,165,233,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_80%,rgba(14,165,233,0.2),transparent_50%)]"></div>
      </div>

      <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 sm:pt-4 pb-12 sm:pb-16">
        <motion.div
          className="text-center max-w-4xl mx-auto"
          variants={prefersReducedMotion ? undefined : containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={prefersReducedMotion ? undefined : itemVariants}
            className="flex justify-center mb-6"
          >
            <div className="group relative inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-primary-50 via-primary-100 to-primary-50 dark:from-primary-900/30 dark:via-primary-800/40 dark:to-primary-900/30 border border-primary-300/50 dark:border-primary-600/50 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary-400/70 dark:hover:border-primary-500/60">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary-500/0 via-primary-500/10 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative text-xs font-medium tracking-wide text-primary-600 dark:text-primary-400 select-none">
                UNDER CONSTRUCTION
              </span>
            </div>
          </motion.div>

          <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
            <h1 className="text-[--text-primary] space-y-2">
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">Research is tough</span>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mt-2">Rework is maddening</span>
            </h1>
          </motion.div>

          <motion.div
            className="mt-8 sm:mt-12"
            variants={prefersReducedMotion ? undefined : itemVariants}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-600 dark:text-primary-400">Don't panic</h2>
            <p className="mt-3 text-xl sm:text-2xl md:text-3xl text-[--text-secondary] italic font-light">
              Scholar42 warps you out
            </p>
          </motion.div>

          <motion.p
            variants={prefersReducedMotion ? undefined : itemVariants}
            className="mt-9 sm:mt-12 max-w-3xl mx-auto text-center text-lg sm:text-xl md:text-2xl leading-snug"
          >
            <span className="text-[--text-secondary]">You don't need another AI writer</span>{" "}

            {/* 把边框直接放到容器上 */}
            <span
              className="mx-1 inline-flex items-baseline rounded-md px-2 py-0.5
               border border-primary-600/45 dark:border-info-300/45"
            >
              <span className="font-semibold text-primary-700 dark:text-info-300">
                Scholar42 is your AI collaborator
              </span>
            </span>
          </motion.p>


          <motion.div
            className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center"
            variants={prefersReducedMotion ? undefined : itemVariants}
          >
            {AUTH_ENABLED ? (
              <Link
                to="/login"
                className="group relative px-8 py-4 text-base sm:text-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto"
              >
                <span className="relative z-10">Try Scholar42</span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            ) : (
              <span className="inline-block px-8 py-4 text-base sm:text-lg font-semibold text-slate-400 dark:text-slate-500 bg-slate-200 dark:bg-slate-700/60 rounded-xl shadow-sm cursor-not-allowed select-none w-full sm:w-auto text-center">
                Coming Soon
              </span>
            )}
            <Link
              to="/about"
              className="px-8 py-4 text-base sm:text-lg font-semibold text-[--text-primary] bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
            >
              Learn More
            </Link>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-400 dark:border-slate-600 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-slate-400 dark:bg-slate-600 rounded-full"></div>
        </div>
      </div>
    </section>
  );
}
